import { useNavigation } from '@react-navigation/native';
import differenceInMonths from 'date-fns/differenceInMonths';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import { LinearGradient } from 'expo-linear-gradient';
import hexToRgba from 'hex-to-rgba';
import fill from 'lodash/fill';
import groupBy from 'lodash/groupBy';
import sum from 'lodash/sum';
import { Fragment, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import {
  BarChart,
  BarChartPropsType,
  LineChart,
  LineChartPropsType,
} from 'react-native-gifted-charts';

import { Icon } from '@oui/app-core/src/components/Icon';
import { PillGroup } from '@oui/app-core/src/components/PillGroup';
import { Label, Text, ThemeText } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { STRESS_BREATHING_ROUND_COLORS } from '@oui/app-core/src/ptsdTheme';
import { useTheme } from '@oui/app-core/src/styles';
import { parseGQLDate, parseGQLDateTime } from '@oui/lib/src/gqlDate';
import { FragmentOf, graphql, readFragment, ResultOf } from '@oui/lib/src/graphql/tada';
import { times } from '@oui/lib/src/times';
import { GQLDateTime } from '@oui/lib/src/types/scalars';

import { StressBreathingExerciseType } from '../../types/navigation';

const Y_AXIS_WIDTH = 25;

export const StressBreathingOverviewFragment = graphql(`
  fragment StressBreathingOverview on Role {
    breathHoldingWidget @include(if: $breathHolding) {
      ID
      stressBreathingExercises(input: {}) {
        practiceID
        practiceValues {
          date
        }
        stressBreathingExercise {
          isComplete
          rounds {
            distressRating
            isComplete
          }
        }
      }
    }
    hyperventilationWidget @include(if: $hyperventilation) {
      ID
      stressBreathingExercises(input: {}) {
        practiceID
        practiceValues {
          date
        }
        stressBreathingExercise {
          isComplete
          rounds {
            distressRating
            isComplete
          }
        }
      }
    }
  }
`);

function TableLabel({ text }: { text: string }) {
  const { theme } = useTheme();
  return <Text text={text} size={15} color={theme.color.gray300} weight="semibold" />;
}

function TableDivider() {
  const { theme } = useTheme();
  return <View style={{ height: 1, width: '100%', backgroundColor: theme.color.gray600 }} />;
}

function TableRow({
  type,
  practice,
}: {
  type: StressBreathingExerciseType;
  practice: NonNullable<
    ResultOf<typeof StressBreathingOverviewFragment>['breathHoldingWidget']
  >['stressBreathingExercises'][number];
}) {
  const { theme } = useTheme();
  const { formatDate } = useI18n();
  const { navigate } = useNavigation();
  return (
    <Pressable
      onPress={() => navigate('StressBreathingShow', { type, practiceID: practice.practiceID })}
      style={{ paddingVertical: 10 }}
    >
      <View row>
        <View style={{ width: 100 }}>
          <Text
            text={formatDate(parseGQLDate(practice.practiceValues.date), {
              month: 'short',
              day: 'numeric',
            })}
            weight="semibold"
            size={15}
          />
        </View>
        <View row style={{ flex: 1 }}>
          {times(5, (i) => (
            <View style={{ flex: 1 }} key={i}>
              <Text
                text={practice.stressBreathingExercise.rounds[i]?.distressRating?.toString() ?? ''}
                size={15}
              />
            </View>
          ))}
        </View>
        <Icon name="caret-right" color={theme.color.primary100} size={16} />
      </View>
    </Pressable>
  );
}

function Legend(props: { data: Array<{ color: string; text: string }> }) {
  return (
    <View
      style={{
        gap: 6,
        flexDirection: 'row',
        paddingHorizontal: 50,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      {props.data?.map((d) => {
        return (
          <View row spacing={8} key={d.color}>
            <View style={{ width: 15, height: 15, borderRadius: 3, backgroundColor: d.color }} />
            <Text size={13} text={d.text} />
          </View>
        );
      })}
    </View>
  );
}

function StressBreathingOverviewTable({
  type,
  data: _data,
}: {
  type: StressBreathingExerciseType;
  data: FragmentOf<typeof StressBreathingOverviewFragment>;
}) {
  const data = readFragment(StressBreathingOverviewFragment, _data);
  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', paddingBottom: 10 }}>
        <View style={{ width: 100 }}>
          <TableLabel text="Date" />
        </View>
        <View style={{ flex: 1 }}>
          <TableLabel text="Distress rating per round" />
          <View row>
            <View style={{ flex: 1 }}>
              <TableLabel text="1" />
            </View>
            <View style={{ flex: 1 }}>
              <TableLabel text="2" />
            </View>
            <View style={{ flex: 1 }}>
              <TableLabel text="3" />
            </View>
            <View style={{ flex: 1 }}>
              <TableLabel text="4" />
            </View>
            <View style={{ flex: 1 }}>
              <TableLabel text="5" />
            </View>
            <View style={{ flex: 1 }} />
          </View>
        </View>
      </View>
      <TableDivider />
      {(data.breathHoldingWidget || data.hyperventilationWidget)?.stressBreathingExercises.map(
        (exercise) => (
          <Fragment key={exercise.practiceID}>
            <TableRow practice={exercise} type={type} />
            <TableDivider />
          </Fragment>
        ),
      )}
    </View>
  );
}

function StressBreathingOverviewGraph({
  data: _data,
}: {
  type: StressBreathingExerciseType;
  data: FragmentOf<typeof StressBreathingOverviewFragment>;
}) {
  const { formatDate } = useI18n();
  const { theme } = useTheme();
  const data = readFragment(StressBreathingOverviewFragment, _data);
  const [tab, setTab] = useState<'recent' | 'all'>('recent');
  const [width, setWidth] = useState<number>();

  const baseData = (
    (data.breathHoldingWidget || data.hyperventilationWidget)?.stressBreathingExercises ?? []
  ).filter((e) => e.stressBreathingExercise.isComplete);

  if (!baseData.length) return null;

  const recentData = baseData
    .slice(0, 5)
    .map((d, groupIndex) => {
      return fill(
        d.stressBreathingExercise.rounds.map((r) => r.distressRating ?? 0),
        0,
        d.stressBreathingExercise.rounds.length,
        4,
      ).map(
        (value, i) =>
          ({
            value,
            spacing: i === 4 ? (groupIndex === 4 ? 0 : 20) : 1,
            frontColor: STRESS_BREATHING_ROUND_COLORS[i],
          }) satisfies NonNullable<BarChartPropsType['data']>[number],
      );
    })
    .flat(1);

  const labels = baseData.flatMap((v) => [
    formatDate(parseGQLDate(v.practiceValues.date), { month: 'numeric', day: 'numeric' }),
    '',
    '',
    '',
    '',
  ]);

  const numMonths = Math.abs(
    differenceInMonths(
      parseGQLDate(baseData[0].practiceValues.date),
      parseGQLDate(baseData[baseData.length - 1].practiceValues.date),
    ),
  );
  const allData = Object.entries(
    groupBy(baseData, (p) =>
      numMonths > 1
        ? startOfMonth(parseGQLDate(p.practiceValues.date)).toISOString()
        : startOfWeek(parseGQLDate(p.practiceValues.date)).toISOString(),
    ),
  )
    .sort((a, b) => {
      return a[0] < b[0] ? -1 : 1;
    })
    .map(([key, entries]) => {
      const entryDate = parseGQLDateTime(key as GQLDateTime);
      const labelDate =
        numMonths > 1
          ? formatDate(startOfMonth(entryDate), { month: 'short' })
          : formatDate(startOfWeek(entryDate), { month: 'numeric', day: 'numeric' });
      const dataPoints = entries.flatMap((e) =>
        e.stressBreathingExercise.rounds.map((r) => r.distressRating),
      );
      return {
        value: sum(dataPoints) / dataPoints.length,
        labelComponent: () => (
          <ThemeText
            fontTheme={theme.typography.small}
            weight="normal"
            text={labelDate}
            textAlign="center"
          />
        ),
      } satisfies NonNullable<LineChartPropsType['data']>[number];
    });

  return (
    <View style={{ gap: 15, paddingVertical: 15 }}>
      <PillGroup
        style={{ paddingHorizontal: 60 }}
        aria-label="Graph time scale"
        value={tab}
        onChangeValue={setTab}
        items={[
          { label: 'Recent', value: 'recent' },
          { label: 'All', value: 'all' },
        ]}
      />
      <View
        style={{
          paddingHorizontal: 20,
          gap: 10,
          width: '100%',
          overflow: 'hidden',
        }}
        onLayout={(e) => {
          if (width !== e.nativeEvent.layout.width) {
            setWidth(e.nativeEvent.layout.width - 40);
          }
        }}
      >
        {tab === 'recent' ? (
          <>
            <Label small text="Distress rating" />
            <BarChart
              barWidth={8}
              data={recentData}
              disablePress
              disableScroll
              endSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
              initialSpacing={8 / 2} // spacing seems to apply twice, so divide by 2
              labelWidth={8 * 5 + 1 * 4} // width of bars + spacing b/t bars in group
              maxValue={10}
              spacing={0}
              xAxisLabelTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              xAxisLabelTexts={labels}
              yAxisLabelTexts={['', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
              yAxisLabelWidth={Y_AXIS_WIDTH}
              width={typeof width === 'number' ? 8 + width - Y_AXIS_WIDTH * 2 : undefined}
              yAxisTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
            />
            <Label small text="Dates" textAlign="center" />
            <View>
              <Legend
                data={STRESS_BREATHING_ROUND_COLORS.map((color, i) => ({
                  color,
                  text: `Round ${i + 1}`,
                }))}
              />
            </View>
          </>
        ) : (
          <>
            <Label small text="Avg distress rating" />
            <LineChart
              data={allData}
              color={theme.color.gray400}
              dataPointsColor={theme.color.gray400}
              dataPointsRadius={5}
              disableScroll
              maxValue={10}
              xAxisLabelTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              xAxisLabelTexts={labels}
              yAxisLabelTexts={['', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
              yAxisLabelWidth={Y_AXIS_WIDTH}
              yAxisTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              width={typeof width === 'number' ? width - Y_AXIS_WIDTH * 2 : undefined}
            />
            <Label small text="Dates" textAlign="center" />
          </>
        )}
      </View>
    </View>
  );
}

/**
 * Renders an overview and list of all stress breathing exercise data.
 */
export const StressBreathingOverview = ({
  type,
  data: _data,
}: {
  type: StressBreathingExerciseType;
  data: FragmentOf<typeof StressBreathingOverviewFragment>;
}) => {
  const { theme } = useTheme();
  return (
    <View testID="StressBreathingOverview">
      <View style={{ paddingBottom: 30 }}>
        <LinearGradient
          colors={[
            hexToRgba(theme.color.accentTwo100, 0),
            hexToRgba(theme.color.accentTwo100, 0.3),
          ]}
          style={StyleSheet.absoluteFillObject}
          start={[0, 0]}
          end={[0, 1]}
        />
        <StressBreathingOverviewGraph type={type} data={_data} />
      </View>
      <View
        style={{
          marginTop: -30,
          paddingLeft: 22,
          paddingTop: 20,
          borderTopEndRadius: 20,
          borderTopStartRadius: 20,
          backgroundColor: 'white',
        }}
      >
        <StressBreathingOverviewTable type={type} data={_data} />
      </View>
    </View>
  );
};
